import React, { createContext, useEffect, useReducer } from 'react';
import jwtDecode from 'jwt-decode';
import axios, { axiosRelayInstance } from '../../axios.js';
import { axiosLoginInstance, axiosEnrollmentAppApi } from '../../axios.js';
import MatxLoading from '../../features/components/customComponents/MatxLoading';
import { useDispatch, useSelector } from 'react-redux';
import { VALIDATE_USER } from '../../Urls.js';
import {
  setUserAccountDetails,
  setUserLoginInfo
} from '../../app/redux/slices/app/userDetailsSlice.js';
import { UserTypes } from '../../features/constants/commonEnum.js';

const initialState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null
};

const isValidToken = accessToken => {
  if (!accessToken) {
    return false;
  }
  const decodedToken = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;
  return decodedToken.exp > currentTime;
};

const setSession = (accessToken, user, rememberMe) => {
  if (accessToken) {
    if (rememberMe) {
      localStorage.setItem('logged_csmyacctk', accessToken);
      localStorage.setItem('logged__csmyaccusr', JSON.stringify(user));
    }
    localStorage.setItem('csmyacctk', accessToken);
    localStorage.setItem('csmyaccusr', JSON.stringify(user));

    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    axiosEnrollmentAppApi.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    // need to remove after demo.
    axiosRelayInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('logged_csmyacctk');
    localStorage.removeItem('logged__csmyaccusr');
    localStorage.removeItem('csmyacctk');
    localStorage.removeItem('csmyaccusr');
    delete axios.defaults.headers.common.Authorization;
    delete axiosRelayInstance.defaults.headers.common.Authorization;
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INIT': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user
      };
    }
    case 'LOGIN': {
      const { user } = action.payload;
      return {
        ...state,
        isAuthenticated: true,
        isLoginLoading: false,
        user
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null
      };
    }
    case 'REGISTER': {
      const { user } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialState,
  method: 'JWT',
  login: () => Promise.resolve(),
  logout: () => { },
  getAcl: () => Promise.resolve()
});

export const AuthProvider = ({ children }) => {
  const reduxDispatch = useDispatch();
  const [state, dispatch] = useReducer(reducer, initialState);

  const selectedAccount = useSelector(state => state?.userDetailSlice?.selectedAccount);

  const userLoginUserInfo = useSelector(state => state?.userDetailSlice?.userAccountDetails?.userLoginInfo);

  const login = async (email, password, rememberMe, impersonate = false, impersonateToken = "") => {
    const response = await axiosLoginInstance.post(VALIDATE_USER, {
      email: email,
      password: password,
      impersonate: impersonate,
      impersonate_token: impersonateToken,
    });

    const user = response?.data?.data;
    setSession(user.token, user, rememberMe);
    reduxDispatch(setUserLoginInfo(user?.user_details));
    if (user?.user_details?.userTypeCode !== UserTypes.CENTRIC) {
      reduxDispatch(setUserAccountDetails(user?.user_details));
    } else {
      reduxDispatch(setUserAccountDetails([]));
    }
    dispatch({
      type: 'LOGIN',
      payload: {
        user
      }
    });
  };

  const logout = () => {
    localStorage.removeItem('csmyacctk');
    localStorage.removeItem('csmyaccusr');
    localStorage.removeItem('logged_csmyacctk');
    localStorage.removeItem('logged__csmyaccusr');
    delete axios.defaults.headers.common.Authorization;
    //setSession(null);
    dispatch({ type: 'LOGOUT' });
    reduxDispatch({ type: 'USER_LOGOUT' });
    window.location.href = '/login';
  };

  const getAcl = moduleName => {
    let roleList = [];
    if (userLoginUserInfo?.userTypeCode === UserTypes.CENTRIC) {
      if (userLoginUserInfo?.user_accounts.length > 0) {
        roleList = userLoginUserInfo?.user_accounts[0]?.roles || []
      }
      else {
        roleList = userLoginUserInfo?.roles || []
      }
    }
    else if (userLoginUserInfo?.userTypeCode === UserTypes.CUSTOMER) {
      roleList = selectedAccount?.roles || []
    }
    else if (userLoginUserInfo?.userTypeCode === UserTypes.BUILDER) {
      roleList = selectedAccount?.roles || userLoginUserInfo?.user_accounts[0]?.roles || []
    }

    let AclList = [];
    roleList.forEach(item => {
      const roleAppFeatures = item?.app_features?.find(a => a.name === moduleName)
      if (roleAppFeatures) {
        AclList.push(roleAppFeatures)
      }
    });

    const resturnData = {
      name: moduleName,
      read: Math.max(...AclList.map(a => a.read)) === 1 ? true : false,
      add: Math.max(...AclList.map(a => a.add)) === 1 ? true : false,
      update: Math.max(...AclList.map(a => a.update)) === 1 ? true : false,
      delete: Math.max(...AclList.map(a => a.delete)) === 1 ? true : false,
    }
    return resturnData;

  };

  useEffect(() => {
    (async () => {
      try {
        const accessToken = window.localStorage.getItem('logged_csmyacctk')
          ? window.localStorage.getItem('logged_csmyacctk')
          : window.localStorage.getItem('csmyacctk');
        const userJson = window.localStorage.getItem('logged_csmyaccusr')
          ? window.localStorage.getItem('logged_csmyaccusr')
          : window.localStorage.getItem('csmyaccusr');

        if (accessToken && userJson && isValidToken(accessToken)) {
          const user = JSON.parse(userJson);
          setSession(accessToken, user, true);
          reduxDispatch(setUserLoginInfo(user?.user_details));
          if (user?.user_details?.userTypeCode !== UserTypes.CENTRIC) {
            reduxDispatch(setUserAccountDetails(user?.user_details));
          } else {
            reduxDispatch(setUserAccountDetails([]));
          }

          dispatch({
            type: 'INIT',
            payload: {
              isAuthenticated: true,
              user
            }
          });
        } else {
          dispatch({
            type: 'INIT',
            payload: {
              isAuthenticated: false,
              user: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INIT',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    })();
  }, []);

  if (!state.isInitialised) {
    return <MatxLoading />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        logout,
        getAcl
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
